import { Box, Divider } from "@mui/material";
import {
  eventAPIS,
  getScheduleErrorText,
  useSlugValidation,
  useWorkflow,
} from "@platformx/authoring-apis";
import { RootState, previewContent } from "@platformx/authoring-state";
import {
  CommonPlateformXDialog,
  ContentPublishPlan,
  Loader,
  MAPPING,
  SchedulePublishUnpublish,
  ScheduleValue,
  ShowToastError,
  ShowToastSuccess,
  applyPublishStep,
  capitalizeFirstLetter,
  toAbsoluteUrl,
  mergeAutoStructureData,
  planContentPublish,
  readSchedule,
  scheduleFromTriggers,
  seedSchedule,
  runPublishSteps,
  useAccess,
  useUserSession,
  workflowKeys,
  writeSchedule,
} from "@platformx/utilities";
import { addMinutes } from "date-fns";
import { memo, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  brandFieldsFromDoc,
  resolveCanonicalPath,
  StoredCanonical,
} from "../../utils/canonicalPath";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Analytics from "../../components/Analytics/Analytics";
import ContentPageScroll from "../../components/ContentPageScroll";
import ContentStatus from "../../components/ContentStatus/ContentStatus";
import { CreateHeader } from "../../components/CreateHeader/CreateHeader";
import FCMNotification from "../../components/FCMNotification/FCMNotification";
import Seo from "../../components/SEO/SEO";
import SocialShare from "../../components/SocialShare/SocialShare";
import { ContentType } from "../../enums/ContentType";
import { CONTENT_STATUS_PERMISSION, NOTIFICATION_PERMISSION } from "../../utils/Constants";
import { contentStatusTitleSubTitles, notificationTitleSubTitles } from "../../utils/Helper";
import { resolveSlugDocType } from "../../utils/slugDocType";
import { useCustomStyle } from "./CreateEvent.styles";
import { EventInstance, EventWhole } from "./CreateEvent.types";
import icons, {
  // CANCEL,
  CATEGORY_CONTENT,
  DRAFT,
  EVENT,
  // IMAGE_URL,
  PAGE_EXIST_POP_UP,
  PATH,
  PUBLISHED,
  PUBLISH_POP_UP,
  SAVE_AS_DRAFT_POP_UP,
  SCROLL,
  SEO,
  // SOCIAL_SHARE,
  // SOCIAL_SHARE_IMG_URL,
  seo,
} from "./Utils/Constants";
import {
  DefEvent,
  EventData,
  analyticsTitleSubTitles,
  fieldValidation,
  getEventToSend,
  getModifiedField,
  getPreviewContentData,
  getTempObj,
  getUpdateEvent,
  isInViewport,
  seoTitleSubTitles,
  socialShareTitleSubTitles,
  updateEventSettings,
  updateStructureData,
} from "./Utils/helper";
import EventChooseTags from "./components/EventChooseTags/EventChooseTags";
import EventImageAndThumbnail from "./components/EventImageAndThumbnail/EventImageAndThumbnail";
import EventTimeAndLocation from "./components/EventTimeAndLocation/EventTimeAndLocation";
import EventTitleDescription from "./components/EventTitleDescription/EventTitleDescription";

const CreateEvent = () => {
  const dispatch = useDispatch();
  const { getWorkflowDetails, workflowRequest } = useWorkflow();
  const { t, i18n } = useTranslation();
  const classes = useCustomStyle();
  const { currentContent } = useSelector((state: RootState) => state.content);
  const [workflow, setWorkflow] = useState({});
  const [getSession] = useUserSession();
  const { userInfo, role } = getSession();
  const { canAccessAction } = useAccess();
  /* Notification settings are driven by the logged-in user's session permissions */
  const hasNotificationAccess = canAccessAction(
    NOTIFICATION_PERMISSION.category,
    NOTIFICATION_PERMISSION.subCategory,
    NOTIFICATION_PERMISSION.action,
  );
  /* Content status is driven by the logged-in user's session permissions */
  const hasContentStatusAccess = canAccessAction(
    CONTENT_STATUS_PERMISSION.category,
    CONTENT_STATUS_PERMISSION.subCategory,
    CONTENT_STATUS_PERMISSION.action,
  );
  const username = `${userInfo.first_name} ${userInfo.last_name}`;
  const createdUser = useRef(username);
  const quizPageUrl = new URL(window.location.href);
  const currentQuizData = useRef(
    quizPageUrl.searchParams.get("path") ? (quizPageUrl.searchParams.get("path") as string) : "",
  );
  const [scrollToView, setScrollToView] = useState("");
  const [showExitWarning, setShowExitWarning] = useState(false);
  const navigate = useNavigate();
  const [previewButton, setPreviewButton] = useState(true);
  const [previewTooltip, setPreviewTooltip] = useState(true);
  const [isFeatured, setIsFeatured] = useState(false);
  const [parentToolTip, setParentToolTip] = useState("");
  const [eventInstance, setEventInstance] = useState<EventInstance | unknown>({});
  const [isLoading, setIsLoading] = useState(false);
  const unsavedChanges = useRef<boolean>(false);
  const updateTempObj = useRef<unknown>({});
  const eventPageUrl = new URL(window.location.href);
  const [isDraft, setIsDraft] = useState<boolean>(true);
  const [draftPageURL, setDraftPageURL] = useState<string>("");
  const currentEventData = useRef(
    eventPageUrl.searchParams.get(PATH) ? (eventPageUrl.searchParams.get(PATH) as string) : "",
  );
  const [openPageExistModal, setOpenPageExistModal] = useState<boolean>(false);
  const [pageStatus, setPageStatus] = useState(DRAFT);
  // The plan of the publish that opened the "page already exists" modal. `pageStatus` alone cannot
  // carry it: a scheduled publish saves as DRAFT, so Yes would otherwise re-save a plain draft.
  const pendingPlan = useRef<ContentPublishPlan | undefined>(undefined);
  const [onSavedModal, setOnSavedModal] = useState(false);
  const [showPublishConfirm, setShowPublishConfirm] = useState(false);
  const [scheduleConfirmation, setScheduleConfirmation] = useState("");
  const [scheduleErrors, setScheduleErrors] = useState<{ publish?: string; unpublish?: string }>(
    {},
  );
  const [eventState, setEventState] = useState<
    typeof EventData & {
      brand_name?: string;
      secondary_brandname?: string[];
      storedCanonical?: StoredCanonical;
    }
  >(EventData);
  const slugValidation = useSlugValidation(resolveSlugDocType("event"));
  const originalSlugRef = useRef<string>("");
  const [enableWorkflowHistory, setEnableWorkflowHistory] = useState<boolean>(false);
  const publishPopup = useRef(PUBLISH_POP_UP);
  const [isError, setIsError] = useState<any>([]);

  const login_user_id = userInfo?.user_id;
  const updateField = (updatedPartialObj) => {
    updateTempObj.current = updatedPartialObj;
    const newTempData = JSON.parse(JSON.stringify(eventInstance));
    const modifiedEvent = getModifiedField(eventState, newTempData, updatedPartialObj);
    setEventInstance(modifiedEvent);
  };

  const eventWholeRef = useRef<EventWhole>(DefEvent);

  const updateCurrentInstance = () => {
    const pageURL = eventWholeRef.current?.title.replace(/[^A-Z0-9]+/gi, "-").toLowerCase();
    const updatedObj = {
      page: pageURL,
      title: eventWholeRef.current?.title,
      short_title: eventWholeRef.current?.short_title,
      description: eventWholeRef.current?.description,
      short_description: eventWholeRef.current?.short_description,
      tags: eventWholeRef.current?.tags,
      current_page_url: `/${pageURL}`,
      settings: {
        ...updateEventSettings(eventWholeRef, eventState, i18n),
      },
    };
    updateField(updatedObj);
  };

  // KIWI-6576. The schedule lives in the four keys on eventState that `fieldValidation` and the
  // settings read-back already use. It is form state only: a trigger exists solely when the schedule
  // rides on the publish call (see publishEvent).
  const handleScheduleChange = (next: ScheduleValue) => {
    setEventState((prev) => writeSchedule(prev, next));
    setScheduleErrors({});
    unsavedChanges.current = true;
  };

  /**
   * Publishes the saved Event; resolves true only if it worked. With no schedule this is the request
   * that was always sent. With one, each step is its own publish call, run in order, and any failure
   * stops here — so the screen can never congratulate the editor on a schedule that did not take
   * effect (it used to show the success dialog after a failed publish too).
   */
  const publishEvent = async (pageURL, plan?: ContentPublishPlan): Promise<boolean> => {
    // Don't remove the data, API call to publish the event
    const send = (step?) =>
      eventAPIS.publishContentType({
        contentType: EVENT,
        input: step ? applyPublishStep({ page: pageURL }, step) : { page: pageURL },
      });
    try {
      if (plan?.isScheduled) {
        await runPublishSteps(plan.steps, send);
        setScheduleConfirmation(
          plan.confirmation ? t(plan.confirmation.key, plan.confirmation.params) : "",
        );
      } else {
        await send();
        setScheduleConfirmation("");
      }
      return true;
    } catch (error: any) {
      const statusCode = error?.networkError?.statusCode || 0;

      if (error?.name === "SchedulePartialFailureError") {
        // The first schedule is already in place; say so, or the editor resubmits straight into
        // "already trigger is initiated".
        ShowToastError(
          `${t("schedule_partial_failure")} ${getScheduleErrorText(error.cause, t)}`,
          statusCode,
        );
      } else if (error?.graphQLErrors?.length > 0) {
        ShowToastError(getScheduleErrorText(error, t), statusCode);
      } else {
        ShowToastError(t("api_error_toast"), statusCode);
      }
      return false;
    } finally {
      setIsLoading(false);
    }
  };
  const [workflowStatus, setWorkflowStatus] = useState(true);
  const [showWorkflowSubmit, setShowWorkflowSubmit] = useState(false);
  const workflowSubmitRequest = async (workflowObj, status) => {
    const { success, workflow_status } = await workflowRequest(workflowObj, status);
    if (success) {
      workflow_status === workflowKeys.publish.toLowerCase() && status === workflowKeys.approve
        ? setShowPublishConfirm(true)
        : setShowWorkflowSubmit(true);
    }
  };

  const createEvent = async (
    pageState,
    IsDuplicate = false,
    isWorkflow = true,
    // props = {},
    // event_step = "",
    plan: ContentPublishPlan | undefined = undefined,
  ) => {
    setIsLoading(true);
    const newTempData = JSON.parse(JSON.stringify(eventInstance));

    const eventToSend = getEventToSend(
      eventState,
      newTempData,
      updateTempObj,
      pageState,
      IsDuplicate,
      isFeatured,
      createdUser.current,
    );
    try {
      const data: any = await eventAPIS.createContentType({
        contenttype: EVENT,
        input: eventToSend,
      });
      unsavedChanges.current = false;
      if (data?.authoring_createContent?.message === "Successfully created!!!") {
        const pageUrl = data?.authoring_createContent?.path.substring(
          data?.authoring_createContent?.path.lastIndexOf("/") + 1,
        );
        eventWholeRef.current.page = pageUrl;
        setDraftPageURL(pageUrl);
        // A scheduled publish is saved as DRAFT (see planContentPublish), so it is the plan, not the
        // saved state, that says this is a publish.
        if (pageState !== PUBLISHED && !plan?.isScheduled) {
          setIsLoading(false);
          if (data?.authoring_createContent?.isExist === true) {
            pendingPlan.current = undefined;
            setOpenPageExistModal(true);
            setPageStatus(pageState);
            setWorkflowStatus(isWorkflow);
          } else {
            if (!isWorkflow) {
              ShowToastSuccess(`${t("event")} ${t("saved_toast")}`);
            }
            setIsDraft(false);
            const { createdBy, title, description } = eventToSend.CommonFields;
            const workflowObj = {
              createdBy,
              title,
              description,
              path: data?.authoring_createContent?.path,
              workflow_status: workflowKeys.draft,
              tag_name: capitalizeFirstLetter(ContentType.Event),
              last_modifiedBy: createdBy,
            };
            if (isWorkflow) {
              workflowSubmitRequest(workflowObj, workflowKeys.approve);
            }
            setWorkflow({ ...workflow, ...workflowObj });
          }
        } else {
          if (data?.authoring_createContent?.isExist === true) {
            pendingPlan.current = plan;
            setOpenPageExistModal(true);
            setPageStatus(pageState);
          } else {
            if (await publishEvent(pageUrl, plan)) {
              setShowPublishConfirm(true);
            }
          }
        }
      }
    } catch (error: any) {
      const statusCode = error?.networkError?.statusCode || 0;

      if (error?.graphQLErrors?.length > 0) {
        ShowToastError(error.graphQLErrors[0].message, statusCode);
      } else {
        ShowToastError(t("api_error_toast"), statusCode);
      }
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (slugValidation.isSubmitCheck) return;
    if (slugValidation.status === "unique") {
      ShowToastSuccess(t("slug_available", { docType: "Event" }));
    } else if (slugValidation.status === "not_unique") {
      ShowToastError(t("slug_not_unique", { docType: "Event" }));
    }
  }, [slugValidation.status, slugValidation.isSubmitCheck]);

  const handleSlugChange = (event) => {
    const { value } = event.target;
    setEventState((prevState) => ({ ...prevState, slug: value }));
    slugValidation.invalidate();
  };

  const handleSlugBlur = () => {
    slugValidation.validate(eventState?.slug || "", { excludeValue: originalSlugRef.current });
  };

  const currentSlug = eventState?.slug || "";
  const slugField = {
    value: currentSlug,
    onChange: handleSlugChange,
    onBlur: handleSlugBlur,
    // The stored canonical is the source of truth: shown as-is until the slug changes, then rebuilt
    // from the document's own primary brand.
    canonicalUrl: currentSlug
      ? toAbsoluteUrl(
          resolveCanonicalPath({
            slug: currentSlug,
            language: i18n.language,
            tagName: "event",
            brandId: eventState?.brand_name,
            stored: eventState?.storedCanonical,
          }),
        )
      : "",
  };

  // Single source of truth for "is the slug allowed to be saved" — called once
  // from every top-level Save/Publish entry point, BEFORE branching into
  // createEvent vs updateEvent. Checking only inside updateEvent left
  // createEvent unchecked; once a document is created in this session,
  // isDraft flips to false and every later save routes into updateEvent even
  // without a page reload, so an empty/unverified slug could still reach it.
  // Enforcing here, ahead of that branch, guarantees the slug is always valid
  // by the time either function runs — no route through this component can
  // skip it.
  const guardSlugThenProceed = async (): Promise<boolean> => {
    if (!currentSlug) {
      ShowToastError(t("slug_required"));
      return false;
    }
    const isUnique = await slugValidation.checkNow(currentSlug, {
      excludeValue: originalSlugRef.current,
    });
    if (!isUnique) {
      ShowToastError(t("slug_url_not_unique", { docType: "Event" }));
      return false;
    }
    // eslint-disable-next-line require-atomic-updates
    originalSlugRef.current = currentSlug;
    return true;
  };

  // Slug is already validated by guardSlugThenProceed before this is ever
  // called (from saveEvent/publish, ahead of the createEvent/updateEvent
  // branch).
  const updateEvent = async (
    pageState,
    isWorkflow = true,
    props = {},
    event_step = "",
    plan: ContentPublishPlan | undefined = undefined,
  ) => {
    setIsLoading(true);
    const newTempData = JSON.parse(JSON.stringify(eventInstance));

    const updateEventToSend = getUpdateEvent(
      eventState,
      newTempData,
      updateTempObj,
      pageState,
      username,
      currentEventData,
      draftPageURL,
      isFeatured,
    );
    try {
      // Don't remove the data, API call to update the event
      await eventAPIS.updateContentType({
        contenttype: EVENT,
        input: updateEventToSend,
      });

      unsavedChanges.current = false;
      // A scheduled publish is saved as DRAFT on purpose, so it must not be taken for a draft save.
      if (pageState && pageState.toLowerCase() === DRAFT.toLowerCase() && !plan?.isScheduled) {
        setIsLoading(false);
        if (!isWorkflow) {
          ShowToastSuccess(`${t("event")} ${t("updated_toast")}`);
        } else {
          workflowSubmitRequest(props, event_step);
        }
        setShowExitWarning(false);
      } else {
        if (await publishEvent(draftPageURL || currentEventData.current, plan)) {
          setShowPublishConfirm(true);
        }
      }
    } catch (error: any) {
      const statusCode = error?.networkError?.statusCode || 0;

      ShowToastError(t("api_error_toast"), statusCode);
      setIsLoading(false);
    }
  };

  const pageExistCloseHandle = () => {
    unsavedChanges.current = true;
    pendingPlan.current = undefined;
    setOpenPageExistModal(false);
    setIsLoading(false);
  };

  const pageExistYesButtonHandle = () => {
    setOpenPageExistModal(false);
    const plan = pendingPlan.current;
    pendingPlan.current = undefined;
    if (plan?.isScheduled) {
      // Retry with the SAME plan: branching on `pageStatus` would drop a scheduled publish (saved as
      // DRAFT) or a scheduled unpublish after a publish-now (saved as PUBLISHED).
      createEvent(plan.saveState, true, false, plan);
    } else if (pageStatus.toLowerCase() === DRAFT.toLowerCase()) {
      createEvent(DRAFT, true, workflowStatus);
    } else if (pageStatus.toLowerCase() === PUBLISHED.toLowerCase()) {
      createEvent(PUBLISHED, true);
    }
  };

  const crossButtonHandle = () => {
    setShowExitWarning(false);
    setOnSavedModal(false);
    navigate(`?path=${eventWholeRef?.current?.page}`);
  };

  const saveEvent = async (status = true, props = {}, event_step = "") => {
    setShowExitWarning(false);
    updateCurrentInstance();
    // const currentDateTime = addMinutes(new Date(), 15).toISOString();
    // if (fieldValidation(eventState, status, currentDateTime, isError, setIsError)) {
    if (!(await guardSlugThenProceed())) return;
    if (!currentEventData.current && isDraft) {
      createEvent(DRAFT, false, status);
    } else {
      updateEvent(DRAFT, status, props, event_step);
    }
    // }
  };

  const publish = async () => {
    updateCurrentInstance();
    const currentDateTime = addMinutes(new Date(), 15).toISOString();
    if (fieldValidation(eventState, true, currentDateTime, isError, setIsError)) {
      // Validate the schedule BEFORE anything is saved, so a bad time never leaves a half-done publish.
      const plan = planContentPublish(
        EVENT,
        readSchedule(eventState),
        (eventInstance as any)?.CommonFields?.page_state || pageStatus,
        new Date(),
        scheduleFromTriggers(eventState).initial,
      );
      if (!plan.verdict.valid) {
        const message = capitalizeFirstLetter(t(plan.verdict.messageKey));
        setScheduleErrors({ [plan.verdict.field]: message });
        ShowToastError(message);
        document.getElementById("schedulePublish")?.scrollIntoView?.({
          behavior: "smooth",
          block: "center",
        });
        return;
      }
      setScheduleErrors({});

      if (!(await guardSlugThenProceed())) return;
      if (!currentEventData.current && isDraft) {
        createEvent(plan.saveState, false, false, plan);
      } else {
        updateEvent(plan.saveState, false, {}, "", plan);
      }
    }
  };

  const returnBack = () => {
    if (unsavedChanges.current === true) {
      setShowExitWarning(true);
    } else {
      navigate("/content/event");
    }
  };

  const scrollHandler = () => {
    if (isInViewport(seo, true)) {
      setParentToolTip(SEO);
    } else {
      const active = icons.find((i) => isInViewport(i.id, false));
      if (active && active.tooltip !== parentToolTip) {
        setParentToolTip(active.tooltip);
      }
    }
  };

  useEffect(() => {
    const dataHolder = document.getElementById("scrollableDiv");
    dataHolder?.addEventListener(SCROLL, scrollHandler);
    const fetchWorkflowDetails = async () => {
      setIsLoading(true);
      if (currentQuizData.current === "" && !Object.keys(currentContent).length) {
        await getWorkflowDetails(
          role,
          login_user_id,
          setWorkflow,
          capitalizeFirstLetter(ContentType.Event),
        );
      }
      setIsLoading(false);
    };
    fetchWorkflowDetails();
    return () => {
      window.removeEventListener(SCROLL, scrollHandler);
    };
  }, []);

  const handelPreview = () => {
    const tempObj = getPreviewContentData(eventState, eventWholeRef, createdUser.current, i18n);
    dispatch(
      previewContent({
        ...tempObj,
        workflow: workflow,
        isDraft: isDraft,
        draftPageURL: draftPageURL,
      }),
    );
    navigate("/content/preview", {
      state: { contentType: MAPPING.Event, pageName: currentEventData?.current || draftPageURL },
    });
  };

  const closeButtonHandle = () => {
    unsavedChanges.current = false;
    navigate("/content/event");
  };

  const handleSocialShareChange = (event) => {
    const { name } = event.target;
    eventWholeRef.current = {
      ...eventWholeRef.current,
      [name]: event.target.value,
    };
    setEventState({
      ...eventState,
      [name]: event.target.value,
    });

    unsavedChanges.current = true;
  };

  const handleSocialShareTagChange = (_, value) => {
    const result = value.filter((str) => str.trim()?.length !== 0);
    const updatedtags = result.filter((c, index) => {
      return result.indexOf(c) === index;
    });
    eventWholeRef.current = {
      ...eventWholeRef.current,
      tagsSocialShare: updatedtags,
    };
    setEventState({
      ...eventState,
      tagsSocialShare: updatedtags,
    });
  };

  const handleSocialShareImageChange = (data) => {
    setEventState((prevState) => {
      return {
        ...prevState,
        socialShareImgURL: data?.original_image?.Thumbnail || "",
      };
    });
    eventWholeRef.current = {
      ...eventWholeRef.current,
      socialShareImgURL: data?.original_image?.Thumbnail || "",
    };
    unsavedChanges.current = true;
  };

  const handleAnalyticsSeoChange = (event, keyName) => {
    eventWholeRef.current = { ...eventWholeRef.current, [keyName]: event?.target?.checked };
    setEventState((prevState) => {
      return {
        ...prevState,
        [keyName]: event?.target?.checked,
      };
    });
  };

  const handleStrucutreDataChange = (structureData) => {
    setEventState((prevState) => {
      return {
        ...prevState,
        structure_data: JSON.stringify(structureData),
      };
    });
  };

  const getContentByPath = async () => {
    try {
      const data: any = await eventAPIS.fetchContent({
        contentType: EVENT,
        path: currentEventData.current,
      });
      setIsLoading(false);
      const contentObj = data?.authoring_getCmsContentByPath;
      const {
        title,
        short_title: shortTitle,
        short_description: shortDesc,
        description,
        tags,
        settingsProperties,
        banner_image: imageUrl,
        path,
        workflow_status,
        stages,
        tag_name,
        last_modifiedBy,
        createdBy,
        task_status,
        user_id,
        user_name,
        is_featured,
        tag_sm,
        slug,
      } = contentObj;
      setIsFeatured(is_featured);
      createdUser.current = createdBy;
      originalSlugRef.current = slug || "";
      const tempObj = {
        ...getTempObj(data, contentObj, eventState),
        slug,
        ...brandFieldsFromDoc(contentObj),
      };
      setWorkflow({
        path,
        workflow_status,
        stages,
        tag_name,
        last_modifiedBy,
        createdBy,
        role,
        title,
        enable: stages?.length > 0 ? true : false,
        login_user_id,
        task_status,
        task_user_id: user_id,
        task_user_name: user_name,
        description,
      });
      // Seeded with the event's existing schedules, shown locked (KIWI-6762). Only eventState: the
      // instance is spread into the save request, and the trigger keys are not document fields.
      setEventState(seedSchedule(tempObj, contentObj));
      setEventInstance({
        ...tempObj,
        page_state: data?.authoring_getCmsContentByPath?.page_state,
      });
      eventWholeRef.current = {
        title: title,
        short_title: shortTitle,
        short_description: shortDesc,
        description: description,
        imageUrl: imageUrl,
        tags: tags,
        socialShareImgURL: settingsProperties?.socialog_image,
        titleSocialShare: settingsProperties?.socialog_title,
        descriptionSocialShare: settingsProperties?.socialog_description,
        tagsSocialShare: settingsProperties?.keywords,
        tag_sm: tag_sm,
      };
    } catch (err: any) {
      const statusCode = err?.networkError?.statusCode || 0;
      ShowToastError("Some thing went wrong", statusCode);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (Object.keys(currentContent).length > 0) {
      eventWholeRef.current = currentContent?.eventWholeRef;
      setEventState(currentContent?.eventState);
      // Restoring from a Preview round-trip (not a fresh fetch) — treat the
      // restored slug as the new baseline so re-saving it unchanged doesn't
      // trigger a stale/empty excludeValue and a false "not unique" result.
      originalSlugRef.current = currentContent?.eventState?.slug || "";
      setWorkflow(currentContent?.workflow);
      setDraftPageURL(currentContent?.draftPageURL);
      setIsDraft(currentContent?.isDraft);
    } else if (currentEventData.current && unsavedChanges.current !== true) {
      setIsLoading(true);
      getContentByPath();
    }
  }, [currentContent]);

  useEffect(() => {
    const { title, description, address, postalCode, original_image, published_images } =
      eventState;
    if (
      title === "" ||
      description === "" ||
      address === "" ||
      postalCode === "" ||
      Object.keys(original_image).length === 0 ||
      published_images.length === 0
    ) {
      setPreviewButton(true);
    } else {
      if (!currentEventData?.current && isDraft) {
        setPreviewTooltip(false);
      } else {
        setPreviewButton(false);
      }
    }
  }, [eventState, isDraft]);

  useEffect(() => {
    const structureData = updateStructureData(eventState);
    setEventState((prevState) => {
      const merged = mergeAutoStructureData(prevState?.structure_data, structureData);
      // Keep the same state object when nothing changed. This effect depends on `eventState`, so
      // always returning a new object re-triggered it forever — a render loop on every Event page
      // (KIWI-6576 found it: it made the screen impossible to render in a test).
      return merged === prevState?.structure_data
        ? prevState
        : { ...prevState, structure_data: merged };
    });
  }, [eventState]);

  const styles = `.sticky-header { position: sticky; top: 0; background-color: #fff; z-index:1} 
  .sticky-header.keyboard-open { position: relative; }`;
  return (
    <>
      <style> {styles} </style>
      {isLoading && <Loader />}
      <Box
        sx={{
          backgroundColor: "#FFF",
        }}></Box>
      <Box>
        <Box>
          <Box>
            <CreateHeader
              // className={isKeyboardOpen ? "sticky-header keyboard-open" : "sticky-header"}
              hasPreviewButton={previewButton}
              showPreview={true}
              handelPreview={handelPreview}
              createText={
                currentQuizData.current
                  ? `${t("edit")} ${t("event")}`
                  : `${t("create")} ${t("event")}`
              }
              publishText={t("publish")}
              saveText={t("save_as_draft")}
              previewText={t("preview")}
              toolTipText={previewTooltip ? t("preview_tooltip") : t("preview_tooltip1")}
              saveVariant='secondaryButton'
              // publish={publish}
              category={CATEGORY_CONTENT}
              subCategory={ContentType.Event}
              workflow={false}
              setEnableWorkflowHistory={setEnableWorkflowHistory}
              setIsFeatured={setIsFeatured}
              isFeatured={isFeatured}
              handleReturn={returnBack}
              handleSaveOrPublish={saveEvent}
              handlePublish={publish}
              publishDisabled={isLoading}
            />
            <Divider></Divider>
          </Box>

          <Box
            sx={{
              position: "relative",
              height: {
                sm: "calc(100vh - 125px)",
                xs: "calc(100vh - 45px)",
              },
              overflowY: "scroll",
              overflowX: "hidden",
            }}
            id='scrollableDiv'>
            {!enableWorkflowHistory && (
              <Box
                sx={{
                  position: "fixed",
                  top: "25%",
                  right: { sm: "5px", xs: 0 },
                  zIndex: 1000,
                }}>
                <ContentPageScroll
                  icons={icons}
                  scrollToView={scrollToView}
                  parentToolTip={parentToolTip}
                />
              </Box>
            )}
            {enableWorkflowHistory ? (
              // <WorkflowHistory
              //   workflow={workflow}
              //   setEnableWorkflowHistory={setEnableWorkflowHistory}
              // />
              <>workflow history</>
            ) : (
              <>
                <EventTitleDescription
                  setSaveButton={() => {}}
                  //setPreviewButton={setPreviewButton}
                  eventWholeRef={eventWholeRef}
                  state={eventState}
                  setState={setEventState}
                  unsavedChanges={unsavedChanges}
                  setPublishButton={() => {}}
                  isError={isError}
                  setIsError={setIsError}
                />
                <EventImageAndThumbnail
                  state={eventState}
                  setState={setEventState}
                  eventWholeRef={eventWholeRef}
                  unsavedChanges={unsavedChanges}
                  isError={isError}
                  setIsError={setIsError}
                />
                <EventTimeAndLocation
                  state={eventState}
                  setState={setEventState}
                  unsavedChanges={unsavedChanges}
                  isError={isError}
                  setIsError={setIsError}
                />

                <EventChooseTags
                  state={eventState}
                  setState={setEventState}
                  eventWholeRef={eventWholeRef}
                  content={currentContent}
                  unsavedChanges={unsavedChanges}
                  isEdit={currentEventData.current ? true : false}
                  setScrollToView={setScrollToView}
                  socialShareExpanded={false}
                />
                <SocialShare
                  titleSubTitles={socialShareTitleSubTitles()}
                  state={eventState}
                  handleOnBlur={handleSocialShareChange}
                  handleChange={() => {}}
                  handleTagChange={handleSocialShareTagChange}
                  handleImageChange={handleSocialShareImageChange}
                />
                <Analytics
                  titleSubTitles={analyticsTitleSubTitles()}
                  state={eventState}
                  handleChange={handleAnalyticsSeoChange}
                />
                <Seo
                  titleSubTitles={seoTitleSubTitles()}
                  state={eventState}
                  handleChange={handleAnalyticsSeoChange}
                  handleStrucutreDataChange={handleStrucutreDataChange}
                  slugField={slugField}
                />
                {hasNotificationAccess ? (
                  <FCMNotification
                    titleSubTitles={notificationTitleSubTitles()}
                    state={eventState}
                    setState={setEventState}
                    unsavedChanges={unsavedChanges}
                    contentRef={eventWholeRef}
                    sectionNumber='08'
                    handleOnBlur={handleSocialShareChange}
                    handleChange={handleAnalyticsSeoChange}
                  />
                ) : null}
                {hasContentStatusAccess ? (
                  <ContentStatus
                    titleSubTitles={contentStatusTitleSubTitles()}
                    state={eventState}
                    setState={setEventState}
                    unsavedChanges={unsavedChanges}
                    contentRef={eventWholeRef}
                    sectionNumber={hasNotificationAccess ? "09" : "08"}
                  />
                ) : null}
                {/* Schedule goes after the last section the user can see. The old schedule section was
                    numbered 04 and collided with Choose Tags, so this number is derived instead. */}
                <Box className={classes.mainStyleWrapper}>
                  <SchedulePublishUnpublish
                    variant='section'
                    sectionNumber={String(
                      8 + Number(hasNotificationAccess) + Number(hasContentStatusAccess),
                    ).padStart(2, "0")}
                    value={readSchedule(eventState)}
                    onChange={handleScheduleChange}
                    errors={scheduleErrors}
                    pending={scheduleFromTriggers(eventState).pending}
                  />
                </Box>
              </>
            )}
          </Box>
        </Box>
      </Box>

      {showExitWarning && (
        <CommonPlateformXDialog
          isDialogOpen={showExitWarning}
          title={t("save_warn_title")}
          subTitle={t("save_warn_subtitle")}
          closeButtonText={t("stay_here")}
          confirmButtonText={t("take_me_out")}
          closeButtonHandle={() => {
            setShowExitWarning(false);
          }}
          confirmButtonHandle={closeButtonHandle}
          modalType='unsavedChanges'
        />
      )}
      <CommonPlateformXDialog
        isDialogOpen={onSavedModal}
        title={t(SAVE_AS_DRAFT_POP_UP.saveAsDraftTitle)}
        subTitle={t(SAVE_AS_DRAFT_POP_UP.saveAsDraftDescription)}
        closeButtonText={t(SAVE_AS_DRAFT_POP_UP.saveAsDraftCloseText)}
        confirmButtonText={t(SAVE_AS_DRAFT_POP_UP.saveAsDraftConfirmText)}
        confirmButtonHandle={() => navigate("/content/event")}
        closeButtonHandle={crossButtonHandle}
        modalType='draft'
      />

      <CommonPlateformXDialog
        isDialogOpen={showPublishConfirm || showWorkflowSubmit}
        title={t(publishPopup.current.publishTitle)}
        subTitle={
          showPublishConfirm
            ? `${t("your")} ${t("event")} ${scheduleConfirmation || t(publishPopup.current.publishDescription)}`
            : t("requested_action")
        }
        confirmButtonText={t(publishPopup.current.publishConfirmText)}
        confirmButtonHandle={() => navigate("/content/event")}
        modalType='publish'
      />
      {openPageExistModal && (
        <CommonPlateformXDialog
          isDialogOpen={openPageExistModal}
          title={t(PAGE_EXIST_POP_UP.saveAsDraftTitle)}
          subTitle={t(PAGE_EXIST_POP_UP.saveAsDraftDescription)}
          closeButtonText={t(PAGE_EXIST_POP_UP.saveAsDraftCloseText)}
          confirmButtonText={t(PAGE_EXIST_POP_UP.saveAsDraftConfirmText)}
          confirmButtonHandle={pageExistYesButtonHandle}
          closeButtonHandle={pageExistCloseHandle}
          crossButtonHandle={pageExistCloseHandle}
          modalType=''
        />
      )}
    </>
  );
};
export default memo(CreateEvent);
